import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { QueryFindAllRefundsAdminArgs, FindAllRefundsAdminQueryQuery, FindAllRefundsAdmin } from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllRefundsAdminQuery = gql`
  query FindAllRefundsAdminQuery(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $searchText: String
    $duration: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllRefundsAdmin(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      searchText: $searchText
      duration: $duration
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      refund {
        id
        amount
        reasonForRefund
        description
        customerName
        transactionId
        createdAt
        reference
        status
        currency
        userId
        createdBy {
          id
          admin{
            id
            merchantProfile{
              companyName
              businessName
            }
          }
        }
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllRefundsAdmin {
  refund: FindAllRefundsAdmin[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreRefunds: boolean;
}

interface Props {
  variables: QueryFindAllRefundsAdminArgs;
  children: (data: WithFindAllRefundsAdmin) => React.ReactElement | null;
}

export const FindAllRefundsAdminController: React.FC<Props> = ({
  children,
  variables,
}) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllRefundsAdminQueryQuery,
    QueryFindAllRefundsAdminArgs
  >(findAllRefundsAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllRefundsAdminData = data?.findAllRefundsAdmin?.refund || [];
  const page = data?.findAllRefundsAdmin?.page || variables.page;
  const pageSize = data?.findAllRefundsAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllRefundsAdmin?.total || 0;
  const totalPages = data?.findAllRefundsAdmin?.totalPages || 0;
  const hasMoreRefunds = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreRefunds) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAllRefundsAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllRefundsAdmin: {
            ...fetchMoreResult.findAllRefundsAdmin,
            data: [
              ...(prev?.findAllRefundsAdmin?.refund || []),
              ...(fetchMoreResult.findAllRefundsAdmin.refund || [])
            ]
          }
        };
      },
    });
  }, [fetchMore, variables, page, hasMoreRefunds]);

  return children({
    refund: findAllRefundsAdminData as any,
    loading,
    loadMore,
    hasMoreRefunds,
    page,
    pageSize,
    total,
    totalPages,
  });
};
